// src/services/api.js
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const BASE_URL = '/api/v1';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Humanize and extract useful error messages from common API shapes
const humanize = (s = '') => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const extractErrorMessage = (data) => {
  if (!data) return '';
  if (typeof data === 'string') return data;

  // JSON:API array of errors with detail
  if (Array.isArray(data.errors) && data.errors.length) {
    if (typeof data.errors[0] === 'string') return data.errors.join(', ');
    if (data.errors[0]?.detail) return data.errors.map(e => e.detail).join(', ');
  }

  // Rails ActiveModel errors object
  if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
    const parts = [];
    for (const [field, msgs] of Object.entries(data.errors)) {
      if (Array.isArray(msgs)) parts.push(`${humanize(field)} ${msgs.join(', ')}`);
      else parts.push(`${humanize(field)} ${msgs}`);
    }
    if (parts.length) return parts.join('; ');
  }

  // Other common arrays
  if (Array.isArray(data.full_messages)) return data.full_messages.join(', ');
  if (Array.isArray(data.messages)) return data.messages.join(', ');

  // Fallback fields
  return data.detail || data.error || data.message || '';
};

const parseError = async (response) => {
  try {
    const json = await response.json();
    const msg = extractErrorMessage(json);
    return msg || `HTTP ${response.status}: ${response.statusText}`;
  } catch {
    try {
      const text = await response.text();
      return text || `HTTP ${response.status}: ${response.statusText}`;
    } catch {
      return `HTTP ${response.status}: ${response.statusText}`;
    }
  }
};

const parseJSON = async (response) => {
  if (response.status === 204) return {}; // No Content
  return response.json();
};

const api = {
  // login method - FIXED to match working version
  login: async (email, password) => {
    const formData = new FormData();
    formData.append('user[email]', email);
    formData.append('user[password]', password);
    
    const response = await fetch(`${API_BASE_URL}/users/sign_in`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const msg = await parseError(response);
      throw new Error(msg || 'Failed to login');
    }

    const data = await response.json();
    return { access_token: data.token };
  },

  // HTTP methods for API calls
  get: async (url) => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      const msg = await parseError(response);
      throw new Error(msg);
    }
    
    return parseJSON(response);
  },

  post: async (url, data) => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const msg = await parseError(response);
      throw new Error(msg);
    }
    
    return parseJSON(response);
  },

  put: async (url, data) => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const msg = await parseError(response);
      throw new Error(msg);
    }
    
    return parseJSON(response);
  },

  delete: async (url) => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      const msg = await parseError(response);
      throw new Error(msg);
    }
    
    return parseJSON(response);
  }
};

export default api;